// src/pages/DeliverablesManagement.jsx
import React, { useRef, useMemo } from "react";
import { Upload, Download, Trash2, AlertCircle, FileText, User, Building2, Loader2, Package, Archive, CalendarDays, X } from "lucide-react";
import { clsx } from "clsx";
import { useDeliverablePage } from "@hooks";
import { PageHeader, FilterBar, DataTable } from "@components"; // 🌟 공통 컴포넌트 임포트

export function DeliverablesManagement() {
  const {
    filters, setFilters, page, onSearch,
    selectedGroup, setSelectedGroup, detailList, detailLoading,
    uploadFile, deleteAllFiles, deleteSingleFile, downloadFile, downloadAllFiles, downloadSingleFile, customSmoothScroll, auth
  } = useDeliverablePage();

  // 🌟 (추가) 하단 산출물 파일 목록 영역을 가리키는 ref 생성
  const fileListRef = useRef(null);

  const handleReset = () => {
    setFilters({
      assignee: '', 
      companyName: '', 
      startDate: '', 
      endDate: '', 
      pageNum: 1 // 페이지도 1페이지로 리셋
    });
  };

  const fileInputRef = useRef(null);

  const canEdit = auth?.role === 'ROLE_ADMIN' || auth?.staffId === selectedGroup?.staffId1;
  const isAdmin = auth?.role === 'ROLE_ADMIN';

  // 🌟 (추가) 그룹 클릭 시 선택 처리 및 부드러운 스크롤 이동 핸들러
  const handleGroupClick = (record) => {
    setSelectedGroup(record);
    // 선택 후 아주 짧은 지연(setTimeout)을 주어 상태 업데이트 후 렌더링된 뒤에 스크롤 이동
    setTimeout(() => {
      customSmoothScroll(fileListRef, 700);
    }, 100);
  };

  const handleUploadClick = (assignNo) => {
    if (!canEdit) return alert("본인이 담당하는 사업장만 산출물을 업로드할 수 있습니다.");
    
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = async (e) => {
      //const file = e.target.files[0];
      const files = Array.from(e.target.files);
      if (files.length > 0) {
        try {
          await uploadFile({ assignNo, files });
          alert("파일 업로드가 완료되었습니다.");
        } catch (error) {
          alert("파일 업로드에 실패했습니다.");
        }
      }
    };
    input.click();
  };

  // 🌟 1. 전체(일괄) 삭제 핸들러
  const handleDeleteAll = async (assignNo, atchFileId) => {
    if (!canEdit) return alert("본인이 담당하는 사업장만 산출물을 삭제할 수 있습니다.");
    if (window.confirm("등록된 모든 산출물 파일을 일괄 삭제하시겠습니까?")) {
      await deleteAllFiles({ assignNo, atchFileId });
    }
  };

  // 🌟 2. 개별 파일 삭제 핸들러
  const handleDeleteSingle = async (assignNo, atchFileId, fileSn) => {
    if (!canEdit) return alert("본인이 담당하는 사업장만 산출물을 삭제할 수 있습니다.");
    if (window.confirm("이 파일을 삭제하시겠습니까?")) {
      await deleteSingleFile({ assignNo, atchFileId, fileSn });
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '-';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  // 🌟 (추가) 백엔드에서 받은 1차원 배열을 assignNo 기준으로 묶어주는 로직
  const groupedDetailList = useMemo(() => {
    if (!detailList || detailList.length === 0) return [];
    
    const groupMap = {};
    detailList.forEach(detail => {
      if (!groupMap[detail.assignNo]) {
        // 처음 나오는 회차 정보 세팅
        groupMap[detail.assignNo] = {
          ...detail,
          files: [], // 파일을 담을 배열
          totalSize: 0
        };
      }
      // 파일 정보가 있으면 배열에 추가
      if (detail.atchFileId && detail.orignlFileNm) {
         groupMap[detail.assignNo].files.push({
           fileSn: detail.fileSn, // ⚠️ Mapper에서 fileSn도 가져오도록 쿼리 수정 필요! (아래 참조)
           orignlFileNm: detail.orignlFileNm,
           fileSize: detail.fileSize,
           creatDt: detail.creatDt
         });
         groupMap[detail.assignNo].totalSize += (detail.fileSize || 0);
      }
    });
    
    // 객체를 다시 배열로 변환하고 회차(visitRound) 순으로 정렬
    return Object.values(groupMap).sort((a, b) => a.visitRound - b.visitRound);
  }, [detailList]);

  const initialHelpText = `사업장 방문 후 작성된 컨설팅 결과 보고서(산출물)를 업로드하고 다운로드하는 페이지입니다.

[주의사항]
'방문 일정 관리' 메뉴에서 해당 방문 회차의 [실시 여부]가 반드시 [실시] 처리되어야만 파일을 업로드할 수 있습니다. 미실시 상태에서는 업로드 버튼이 눌리지 않습니다.

[이용 순서]
1. 상단 '방문 기록 그룹' 목록에서 사업장(회사명)을 클릭하여 선택하세요.
2. 하단 '산출물 파일 목록'에 선택한 사업장의 방문 회차들이 나타납니다.
3. 우측 끝의 [업로드] 버튼을 눌러 내 컴퓨터에 있는 파일을 등록하세요.
4. 방문을 실시했으나 파일을 올리지 않은 회차에는 빨간색 [미등록] 경고가 표시됩니다.`;

  // 🌟 상단 그룹 테이블 컬럼 정의
  const groupTableColumns = [
    { label: "방문 담당자" },
    { label: "회사명" },
    { label: "최초 시작일", align: "text-center" },
    { label: "최종 완료일", align: "text-center" }
  ];

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 w-full max-w-full">
      
      {/* 1. Page Header */}
      <PageHeader 
        icon={Package} 
        iconBgColor="bg-blue-600"
        title="산출물 파일 관리" 
        description="수행요원별 사업장 방문 결과(산출물)를 업로드하고 관리합니다." 
        auth={auth}                       // (필수) 권한 확인용 (관리자면 수정버튼 뜸)
        helpId="deliverables"             // (필수) DB 저장 및 조회 시 식별용 ID
        initialHelpText={initialHelpText} // (선택) DB에 내용이 없을 때 띄워줄 기본 텍스트
      />

      {/* 2. Filter Bar */}
      <FilterBar onReset={handleReset}>
      {isAdmin && (
        <div className="space-y-1.5 flex-1 max-w-[250px]">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1">방문 담당자</label>
          <div className="relative">
            <input type="text" placeholder="담당자명 검색" className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" value={filters.assignee} onChange={(e) => setFilters(p => ({ ...p, assignee: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && onSearch()} />
            <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          </div>
        </div>
        )}

        <div className="space-y-1.5 flex-1 max-w-[250px]">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">회사명</label>
          <div className="relative">
            <input type="text" placeholder="회사명 검색" className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" value={filters.companyName} onChange={(e) => setFilters(p => ({ ...p, companyName: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && onSearch()} />
            <Building2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          </div>
        </div>
        {/* 🌟 (추가) 방문 기간 검색 필터 */}
        <div className="space-y-1.5 flex-1 max-w-[320px]">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1">최초 방문 기간</label>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <input 
                type="date" 
                className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" 
                value={filters.startDate} 
                onChange={(e) => setFilters(p => ({ ...p, startDate: e.target.value }))} 
              />
              <CalendarDays className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            </div>
            <span className="text-slate-400 font-medium">~</span>
            <div className="relative flex-1">
              <input 
                type="date" 
                className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" 
                value={filters.endDate} 
                onChange={(e) => setFilters(p => ({ ...p, endDate: e.target.value }))} 
              />
              <CalendarDays className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            </div>
          </div>
        </div>
        {/* 🌟 (추가) 전체 다운로드 버튼 (오른쪽 끝으로 밀기) */}
        <div className="flex-1 flex justify-end items-end h-[62px]">
          <button 
            onClick={downloadAllFiles}
            className="flex items-center gap-2 h-10 px-4 rounded-lg bg-slate-800 text-white text-sm font-semibold hover:bg-slate-900 transition-colors shadow-sm"
          >
            <Archive className="h-4 w-4" /> 전체 산출물 다운로드
          </button>
        </div>
      </FilterBar>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        
        {/* 3. Middle Section - Visit Records List (Grouped) - 공통 DataTable 사용 */}
        <div className="lg:col-span-3 space-y-4">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2"><FileText className="h-5 w-5 text-blue-600" /> 방문 기록 그룹</h3>
          
          <DataTable 
            columns={groupTableColumns}
            page={page}
            itemName="그룹"
            onPageChange={(newPage) => setFilters(p => ({ ...p, pageNum: newPage }))}
            emptyMessage="조회된 데이터가 없습니다."
            renderRow={(record) => {
              const displayName = record.staffNm || record.staffId1 || '미배정';
              const isSelected = selectedGroup?.bizMgtNo === record.bizMgtNo && selectedGroup?.staffId1 === record.staffId1;
              return (
                <tr 
                  key={`${record.bizMgtNo}-${record.staffId1}`} 
                  onClick={() => handleGroupClick(record)}
                  className={clsx("cursor-pointer transition-colors", isSelected ? "bg-blue-50/80 hover:bg-blue-100/80" : "hover:bg-slate-50")}
                >
                  <td className="px-6 py-4 font-medium text-slate-900">
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700">
                      {displayName}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-600">{record.companyName}</td>
                  <td className="px-6 py-4 text-center text-slate-500">{record.startDate ? record.startDate.substring(0, 10) : '-'}</td>
                  <td className="px-6 py-4 text-center text-slate-500">{record.completionDate ? record.completionDate.substring(0, 10) : '-'}</td>
                </tr>
              );
            }}
          />
        </div>

        {/* 4. Bottom Section - File Management Area (별도 UI 유지) */}
        <div className="lg:col-span-3 space-y-4" ref={fileListRef}>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Upload className="h-5 w-5 text-blue-600" />
              산출물 파일 목록
              <span className="ml-2 text-sm font-normal text-slate-500">
                선택된 기록: <span className="font-semibold text-blue-600">{selectedGroup ? selectedGroup.companyName : '목록에서 사업장을 선택해주세요'}</span>
              </span>
            </h3>
          </div>

          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 w-20 text-center">회차</th>
                    <th className="px-6 py-4 min-w-[300px]">파일명 / 진행상태</th>
                    <th className="px-6 py-4 text-center">시작일</th> 
                    <th className="px-6 py-4 text-center">완료일</th>
                    <th className="px-6 py-4 text-right">크기</th>
                    <th className="px-6 py-4 text-center">업로드일</th>
                    <th className="px-6 py-4 text-right">관리</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {!selectedGroup ? (
                     <tr><td colSpan="7" className="py-10 text-center text-slate-500">위 목록에서 조회할 항목을 선택해주세요.</td></tr>
                  ) : detailLoading ? (
                     <tr><td colSpan="7" className="py-10 text-center text-slate-500"><Loader2 className="animate-spin mx-auto h-6 w-6" /></td></tr>
                  ) : groupedDetailList.length === 0 ? (
                     <tr><td colSpan="7" className="py-10 text-center text-slate-500">해당 그룹에 등록된 방문 회차가 없습니다.</td></tr>
                  ) : groupedDetailList.map((detail) => {
                    
                    const isExecuted = !!detail.executeDtm; 
                    const hasFile = !!detail.atchFileId;    
                    const isMissing = isExecuted && !hasFile; 

                    const visitDate = detail.visitDate ? detail.visitDate.toString().substring(0, 10) : '-';
                    const executeDtm = detail.executeDtm ? detail.executeDtm.toString().substring(0, 10) : '-';

                    return (
                      <tr key={detail.assignNo} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-4 text-center font-medium text-slate-700">{detail.visitRound}차</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-800">{detail.visitRound}차 컨설팅 결과물 목록</span>
                            {isExecuted ? (
                              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-600 ring-1 ring-inset ring-blue-600/20">실시</span>
                            ) : (
                              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">미실시</span>
                            )}
                            {isMissing && (
                              <span className="flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600 ring-1 ring-inset ring-red-600/20">
                                <AlertCircle className="h-3 w-3" /> 미등록
                              </span>
                            )}
                          </div>
                          {hasFile && (
                            <div className="max-h-[40px] overflow-y-auto mt-2 pr-2 space-y-1 
                            [&::-webkit-scrollbar]:w-1
                            [&::-webkit-scrollbar-track]:bg-transparent
                            [&::-webkit-scrollbar-thumb]:bg-slate-200
                            [&::-webkit-scrollbar-thumb]:rounded-full
                            hover:[&::-webkit-scrollbar-thumb]:bg-slate-300">
                              {detail.files.map((f, idx) => (
                               <div key={idx} className="flex items-center text-xs text-slate-500">
                                 <span 
                                   className="truncate max-w-[420px] cursor-pointer hover:text-blue-600 hover:underline transition-colors" 
                                   title={`${f.orignlFileNm} 다운로드`}
                                   onClick={() => downloadSingleFile(detail.atchFileId, f.fileSn, f.orignlFileNm)}
                                 >
                                   ↳ {f.orignlFileNm}
                                 </span>
                                 {canEdit && (
                                   <button 
                                     onClick={() => handleDeleteSingle(detail.assignNo, detail.atchFileId, f.fileSn)}
                                     className="ml-1 p-1 text-slate-400 hover:bg-red-50 hover:text-red-500 rounded transition-colors shrink-0"
                                     title="파일 개별 삭제"
                                   >
                                     <X className="h-3 w-3" />
                                   </button>
                                 )}
                               </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center text-slate-600">{visitDate}</td>
                        <td className="px-6 py-4 text-center text-slate-600">{executeDtm}</td>
                        <td className="px-6 py-4 text-right text-slate-500 font-mono text-xs">{formatBytes(detail.fileSize)}</td>
                        <td className="px-6 py-4 text-center text-slate-500 font-mono text-xs">{detail.creatDt ? detail.creatDt.substring(0, 10) : '-'}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                          
                              <button 
                                onClick={() => handleUploadClick(detail.assignNo)} 
                                disabled={!isExecuted || !canEdit}
                                title={!canEdit ? "수정 권한이 없습니다." : (!isExecuted ? "방문을 '실시' 처리한 후 업로드할 수 있습니다." : "파일 업로드")}
                                className={clsx(
                                  "inline-flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-medium shadow-sm transition-colors",
                                  isExecuted && canEdit ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-slate-200 text-slate-400 cursor-not-allowed"
                                )}
                              >
                                <Upload className="mr-1.5 h-3 w-3" /> {hasFile ? '추가 업로드' : '업로드'}
                              </button>
                            {hasFile && (
                                <button 
                                  onClick={() => {
                                    // 🌟 파일이 1개면 원본 파일명 그대로, 2개 이상일 때만 zip 이름으로 변경!
                                    const downloadName = detail.files.length === 1 
                                      ? detail.files[0].orignlFileNm 
                                      : `${detail.visitRound}차_컨설팅결과보고서.zip`;
                                      
                                    downloadFile(detail.atchFileId, downloadName);
                                  }}
                                  className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 transition-colors"
                                >
                                  <Download className="mr-1.5 h-3 w-3" /> 다운로드
                                </button>
                                )}
                                {canEdit && (
                                  <button 
                                    onClick={() => handleDeleteAll(detail.assignNo, detail.atchFileId)} 
                                    disabled={!hasFile} // canEdit은 이미 부모 조건문에 있으므로 생략 가능
                                    title={hasFile ? "일괄 삭제" : "삭제할 파일이 없습니다"}
                                    className={clsx(
                                      "inline-flex items-center justify-center rounded-md px-2 py-1.5 transition-colors",
                                      hasFile 
                                        ? "text-slate-400 hover:bg-red-50 hover:text-red-600 cursor-pointer" // 파일이 있을 때: 빨간색 호버 효과
                                        : "text-slate-200 opacity-50 cursor-not-allowed" // 파일이 없을 때: 연한 회색 및 금지 커서 (호버 효과 없음)
                                    )}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                             
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="border-t border-slate-100 bg-slate-50 px-6 py-4">
               <p className="text-xs text-slate-500 text-center flex items-center justify-center gap-2">
                 <AlertCircle className="h-4 w-4 text-slate-400" />
                 방문이 '실시' 처리되었으나 서류가 업로드되지 않으면 '미등록' 경고가 표시됩니다.
               </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}